import React, { useCallback, useEffect, useMemo, useState } from "react";

import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box, Stack, Typography } from "@mui/material";
import { BinDeleteIcon } from "assets/material-custom-icons";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { NavigateLink } from "components/Navigate-link/NavigateLink";
import { Menu } from "components/popups";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useFormatDateHook } from "hooks";
import { MaterialReactTable, MRT_ColumnDef, useMaterialReactTable } from "material-react-table";
import { useDealActions, useDealQueriesSelect } from "store";
import { CurrencyType, DealsTable } from "store/types";

import { currencySign } from "../../../constants";
import { ColumnsSetMenu } from "./ColumnsSetMenu";

const tablePaperSx = {
  boxShadow: "0 1px 2px 0 #1212171a !important",
  borderRadius: "8px"
} as const;

const tableContainerSx = {
  maxHeight: "100%",
  border: "1px solid #DBE8F5",
  borderRadius: "8px"
} as const;

const tableHeadCellSx = {
  bgcolor: "#dfe6e9 !important",
  borderRight: "1px solid #DBE8F5",
  borderBottom: "1px solid #DBE8F5",
  ":last-child": {
    borderRight: "none"
  },
  padding: "8px 12px",
  ".Mui-TableHeadCell-Content": {
    display: "flex",
    flexDirection: "row-reverse",
    alignItems: "center",
    gap: "4px",
    ".Mui-TableHeadCell-Content-Labels": {
      flexGrow: 1,
      minWidth: 0,
      overflow: "hidden",
      ".Mui-TableHeadCell-Content-Wrapper": {
        flexGrow: 1,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }
    }
  },
  ".MuiIconButton-root": {
    transition: "opacity 0.3s linear",
    opacity: 0,
    flexShrink: 0
  },
  ":hover": {
    ".MuiIconButton-root": {
      opacity: 1
    }
  },
  ".Mui-TableHeadCell-ResizeHandle-Wrapper": {
    flexShrink: 0,
    position: "absolute",
    zIndex: 4
  }
} as const;

const tableBodyCellSx = {
  bgcolor: "#fff !important",
  borderRight: "1px solid #DBE8F5",
  borderBottom: "1px solid #DBE8F5",
  ":last-child": {
    borderRight: "none"
  },
  padding: "8px 12px",
  fontSize: "16px",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  ":hover": {
    textOverflow: "ellipsis"
  }
} as const;

const tablePaperProps = { sx: tablePaperSx };
const tableContainerProps = { sx: tableContainerSx };
const tableHeadCellProps = { sx: tableHeadCellSx };
const tableBodyCellProps = { sx: tableBodyCellSx };

const rowVirtualizerOptions = {
  estimateSize: () => 32,
  overscan: 6,
  initialRect: { width: 0, height: 480 }
};

const DragHandle = () => <DragIndicatorIcon sx={{ fontSize: "16px" }} />;
const tableIcons = { DragHandleIcon: DragHandle };

export const Table: React.FC = () => {
  const { getDealsTableActions, updateQueries, updateTableColumnSettingsAction } = useDealActions();
  const formatDate = useFormatDateHook();

  const queries = useDealQueriesSelect();

  const { action, loading, data, setData } = useActionHook(getDealsTableActions);

  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([]);

  useEffect(() => {
    action({
      ...queries,
      columns: queries.tableColumns
    });
    setSorting([]);
  }, [queries.order_by, queries.deal_title, queries.filters]);

  const amount = (dealMinTicket: number | string, currency: CurrencyType) => {
    if (dealMinTicket) {
      return `${currencySign[currency]}${Number(dealMinTicket)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
    } else return " -";
  };

  const columns = useMemo<MRT_ColumnDef<DealsTable["rows"][0]>[]>(() => {
    const result: MRT_ColumnDef<DealsTable["rows"][0]>[] = [];

    if (!data?.columns) return result;

    data.columns.forEach((column, index) => {
      result.push({
        id: column.key,
        accessorKey: column.key,
        header: column.title,
        enableColumnActions: false,
        enableColumnOrdering: index !== 0,
        enableSorting: true,
        size: 150,
        minSize: 100,
        Header: ({ column }) => {
          return (
            <Stack
              sx={{
                flexDirection: "row",
                alignItems: "center",
                gap: "4px",
                justifyContent: "space-between",
                minWidth: 0,
                width: "100%",
                overflow: "hidden"
              }}
            >
              <Stack
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "4px",
                  minWidth: 0,
                  overflow: "hidden",
                  flex: "1 1 auto"
                }}
              >
                <MaterialTooltip title={column.columnDef.header}>
                  <Typography
                    noWrap
                    sx={{
                      fontWeight: 500,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    {column.columnDef.header}
                  </Typography>
                </MaterialTooltip>
                {index === 0 && (
                  <span
                    style={{ cursor: "pointer", display: "flex", alignItems: "center", flexShrink: 0 }}
                    onClick={column.getToggleSortingHandler()}
                  ></span>
                )}
              </Stack>
              {index !== 0 && (
                <Box component="span" sx={{ flexShrink: 0 }}>
                  <Menu
                    iconButtonProps={{ size: "sm" }}
                    options={[
                      {
                        title: "Remove",
                        icon: <BinDeleteIcon />,
                        onClick: () => handleDeleteColumn(column.columnDef.id)
                      }
                    ]}
                  />
                </Box>
              )}
            </Stack>
          );
        },
        Cell: ({ cell, row }) => {
          if (column.key === "title") {
            return (
              <MaterialTooltip title={cell.getValue<string>()}>
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                    color: "#26a6ae"
                  }}
                >
                  <NavigateLink
                    allowNewTab={true}
                    route="deal"
                    params={{ deal_id: row.original.id }}
                    style={{ color: "#26a6ae" }}
                  >
                    <>{cell.getValue<string>()}</>
                  </NavigateLink>
                </span>
              </MaterialTooltip>
            );
          }

          if (column.key === "created_at") {
            return (
              <MaterialTooltip title={formatDate({ date: cell.getValue<string>() })}>
                <Typography
                  noWrap
                  sx={{
                    fontWeight: 400,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {formatDate({ date: cell.getValue<string>() })}
                </Typography>
              </MaterialTooltip>
            );
          }

          if (column.key === "minimum_ticket") {
            return (
              <MaterialTooltip
                title={amount(
                  Number(cell.getValue<string | number>()).toFixed(),
                  (row.original.currency as CurrencyType) || "USD"
                )}
              >
                <Typography
                  noWrap
                  sx={{
                    fontWeight: 400,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {amount(
                    Number(cell.getValue<string | number>()).toFixed(),
                    (row.original.currency as CurrencyType) || "USD"
                  )}
                </Typography>
              </MaterialTooltip>
            );
          }

          return (
            <MaterialTooltip title={cell.getValue<string>()}>
              <Typography
                noWrap
                sx={{
                  fontWeight: 400,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}
              >
                {cell.getValue<string>()}
              </Typography>
            </MaterialTooltip>
          );
        }
      });
    });

    return result;
  }, [data]);

  const onColumnOrder = useCallback(
    (orderedKeys: string[]) => {
      if (!data) return;

      updateTableColumnSettingsAction(orderedKeys);

      setData((prevState) => {
        if (!prevState) return prevState;

        const columnKeysObj: Record<string, DealsTable["columns"][0]> = prevState.columns.reduce(
          (acc, col) => ({ ...acc, [col.key]: col }),
          {}
        );

        const updatedColumnsOrder = orderedKeys.map((colKey) => columnKeysObj[colKey]);
        return { ...prevState, columns: updatedColumnsOrder };
      });
      requestAnimationFrame(() => {
        (document.activeElement as HTMLElement)?.blur();
      });
    },
    [data, setData]
  );

  const handleSubmitColumnsSet = useCallback(
    (colKeys: string[]) => {
      let orderedColumns: string[] = [];

      const currentColumns: string[] | undefined = data?.columns.map((item) => item.key);

      if (!currentColumns) {
        orderedColumns = colKeys;
      } else {
        // This is done to keep order of initial columns set
        // We align new columns set according to previous
        // All newly added columns will be at the end
        // new Set is to get rid off duplicated column keys after concat the arrays
        orderedColumns = Array.from(
          new Set([...currentColumns.filter((currentColumn) => colKeys.includes(currentColumn)), ...colKeys])
        );
      }

      updateQueries({ tableColumns: orderedColumns });
      action({
        ...queries,
        columns: orderedColumns
      });
    },
    [queries, data]
  );

  const handleDeleteColumn = useCallback(
    (columnKey: string) => {
      if (!data) return;
      updateQueries({ tableColumns: data.columns.map((item) => item.key).filter((col) => col !== columnKey) });
      action({
        ...queries,
        columns: data.columns.map((item) => item.key).filter((col) => col !== columnKey)
      });
    },
    [data, queries]
  );

  const tableState = useMemo(
    () => ({
      columnOrder: data?.columns.map((col) => col.key) || [],
      isLoading: loading,
      sorting
    }),
    [data, loading, sorting]
  );

  const tableData = useMemo(() => data?.rows || [], [data?.rows]);

  const table = useMaterialReactTable<DealsTable["rows"][0]>({
    layoutMode: "grid",
    columns,
    data: tableData,
    state: tableState,
    onSortingChange: setSorting,
    enablePagination: false,
    enableColumnOrdering: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    memoMode: "cells",

    // --------------------------- Elements ---------------------------------
    enableRowVirtualization: true,
    rowVirtualizerOptions,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableTableFooter: false,
    enableStickyHeader: true,
    onColumnOrderChange: onColumnOrder as any,
    // ---------------------------- Styling ---------------------------------
    muiTablePaperProps: tablePaperProps,
    muiTableContainerProps: tableContainerProps,
    muiTableHeadCellProps: tableHeadCellProps,
    muiTableBodyCellProps: tableBodyCellProps,
    icons: tableIcons
  });

  return (
    <Stack
      sx={{
        borderRadius: "8px",
        overflow: "hidden",
        bgcolor: "#f5f7f7",
        width: "100%",
        position: "relative"
        // pr: "8px"
      }}
    >
      <AsyncContentMui isLoading={loading} spinnerProps={{ size: 150 }}>
        <MaterialReactTable table={table} />
      </AsyncContentMui>
      {data && (
        <ColumnsSetMenu
          iconButtonStyles={{ position: "absolute", right: "-1px", top: "10px", zIndex: 2 }}
          selectedKeys={data.columns.map((col) => col.key)}
          onSubmit={handleSubmitColumnsSet}
        />
      )}
    </Stack>
  );
};
